import * as React from "react";
import { useContext } from "react";
import { GridDataStateChangeEvent } from "@progress/kendo-react-grid";
import { DataResult, State } from "@progress/kendo-data-query";
import { useTheme } from "@mui/material";
import StyledKendoGrid from "../../../../../Grid/StyledKendoGrid";
import ReturnPipelineGridColumns from "./ReturnPipelineGridColumns";
import InlineEditProps from "../../../../../Grid/InlineEdit/InlineEditProps";
import CommandColumn from "../../../../../Grid/ControlColumn/CommandColumn";
import { CommandDeleteRowButton } from "../../../../../Grid/ControlColumn/CommandDeleteRowButton";
import commandDeleteRow from "../../../../../Grid/ControlColumn/CommandDeleteRow";
import { EditProjectContext } from "../../../EditProjectContext";
import { isProjectReadOnly } from "../../../IsProjectReadOnly";
import { InitialState } from "../../../../../Grid/InitialDataState";
import { PdsrClientViewModel } from "../../../../ViewModel/PdsrClientViewModel";
import { removePdsrOnThisRouteFromRoutingTable } from "./RemovePdsrOnThisRouteFromRoutingTable";
import { isArrayUndefinedOrEmpty } from "../../../../../Utils/arrays";

const idField = "PdsrId";

type Props = {
  pdsrsOnThisRoute: Array<PdsrClientViewModel>;
  setPdsrsOnThisRoute: (pdsrsOnThisRoute: Array<PdsrClientViewModel>) => void;
  odcGsdbsForColumns: Array<string> | undefined;
  ftlRouteNumber: string | undefined;
  volumeDataUpload: React.ReactNode;
  isReadOnly: boolean;
};

export default function ReturnPipeline({
  pdsrsOnThisRoute,
  setPdsrsOnThisRoute,
  odcGsdbsForColumns,
  ftlRouteNumber,
  volumeDataUpload,
  isReadOnly,
}: Props) {
  const editProjectContext = useContext(EditProjectContext);
  const { project } = editProjectContext;

  const dataResult: DataResult = {
    data: pdsrsOnThisRoute,
    total: pdsrsOnThisRoute.length,
  };

  const [dataState, setDataState] = React.useState<State>(InitialState);

  const onDataResultChanged = (newResult: DataResult) => {
    setPdsrsOnThisRoute(newResult.data);
  };

  const [editState, setEditState] = React.useState<number>();
  const inlineEditProps = InlineEditProps({
    idField,
    dataResult,
    setEditState,
    onDataReceived: onDataResultChanged,
    isReadonly: isProjectReadOnly(project),
  });

  const commandColumn = CommandColumn({
    // eslint-disable-next-line react/no-unstable-nested-components
    commands: (dataItem: PdsrClientViewModel) => [
      <CommandDeleteRowButton
        disabled={isProjectReadOnly(project) || isReadOnly}
        key="CommandRemoveButton"
        onClick={() => {
          commandDeleteRow({
            idField,
            dataResult,
            setDataResult: onDataResultChanged,
            dataItem,
          });

          removePdsrOnThisRouteFromRoutingTable(
            dataItem.ContainerNetwork,
            dataItem.SupplierGsdb,
            dataItem.PlantGsdb,
            dataItem.DockCode,
            ftlRouteNumber,
            editProjectContext
          );
        }}
      />,
    ],
    showFooterCell: !isArrayUndefinedOrEmpty(pdsrsOnThisRoute),
  });

  const theme = useTheme();
  return (
    <div style={{ marginBottom: "1.5rem" }}>
      <h4 className="k-h4">
        Return Pipeline
        <div
          style={{
            display: "inline-block",
            float: "right",
          }}
        >
          {volumeDataUpload}
        </div>
      </h4>
      <StyledKendoGrid
        muiTheme={theme}
        data={{
          data: dataResult.data
            .filterBy(dataState)
            .orderBy(dataState)
            .paged(dataState)
            .withEditfield(idField, editState),
          total: dataResult.total,
        }}
        sortable
        pageable
        resizable
        {...dataState}
        onDataStateChange={(e: GridDataStateChangeEvent) => {
          setDataState(e.dataState);
        }}
        {...inlineEditProps}
      >
        {[
          ...ReturnPipelineGridColumns({
            odcGsdbsForColumns,
            ftlRouteNumber,
            pdsrsOnThisRoute,
            isReadOnly,
          }),
          commandColumn,
        ]}
      </StyledKendoGrid>
    </div>
  );
}
